// Raw context + value type for the settings dialog, deliberately split from
// SettingsModalProvider.
//
// Same reason as `contexts/authContextRaw.ts` and `consent/consentContextRaw.ts`:
// the provider's import graph reaches SettingsModal → SettingsForm →
// utils/supabase, ProfileContent, the rules editor and the child dialogs. The
// vitest harness needs to satisfy `useSettingsModal()` in ~500 files, and
// pulling that whole graph (and its module-level side effects) into every one
// of them destabilised unrelated suites under `isolate: false`.
//
// Consumers that only need the context — the harness, and any future
// light-weight reader — import from here. Only code that actually renders the
// dialog imports the provider.

import { createContext } from 'react'

import type { SettingsSectionId } from './settingsSections'

export interface SettingsPaymentIntent {
  status: 'success'
  orderId: string | null
}

/**
 * How the dialog was opened. Consumed by the purchase-funnel event
 * `settings.usage_and_tokens.view` (#3503), which needs to distinguish a user
 * who went looking for credits from one who was sent to them by a link.
 *
 * The provider already knows — `open()` and the deep-link effect are separate
 * paths — this just surfaces it instead of making the billing section guess
 * from a query param the deep-link parser has already consumed.
 */
export type SettingsEntryPoint = 'user_menu' | 'settings_deeplink'

/** A view inside the Teams section that `open()` can land on directly. */
export type SettingsTeamsView = 'create'

export interface SettingsOpenOptions {
  /**
   * Open the Teams section straight into this view rather than its overview.
   * Used by the profile menu's "Create team" row, which would otherwise land
   * on the overview and make the user find the create link a second time.
   */
  teamsView?: SettingsTeamsView
}

export interface SettingsModalContextValue {
  isOpen: boolean
  activeSection: SettingsSectionId
  /** Open the dialog on `section` (default: Profile). */
  open: (section?: SettingsSectionId, options?: SettingsOpenOptions) => void
  close: () => void
  /** Payment round-trip payload, when the user arrived from checkout. */
  payment: SettingsPaymentIntent | undefined
  /** How the dialog was opened, for funnel attribution. */
  entryPoint: SettingsEntryPoint
  /** Dismiss the success panel and strip its query params. */
  clearPayment: () => void
  /** Teams view requested by the last `open()`, until TeamsSection applies it. */
  teamsView: SettingsTeamsView | undefined
  /** Called by TeamsSection once it has switched to `teamsView`. */
  clearTeamsView: () => void
}

export const SettingsModalContext = createContext<SettingsModalContextValue | null>(null)

/** Inert value: satisfies `useSettingsModal()` and renders nothing. */
export const INERT_SETTINGS_MODAL: SettingsModalContextValue = {
  isOpen: false,
  activeSection: 'profile',
  open: () => {},
  close: () => {},
  payment: undefined,
  entryPoint: 'user_menu',
  clearPayment: () => {},
  teamsView: undefined,
  clearTeamsView: () => {},
}
